/* ==========================================================================
   UltraWheels — product.js
   Variant selection (selects + swatches), media gallery, tabs,
   read-more toggle and related products.
   ========================================================================== */

(function () {
  'use strict';

  /* --- Media gallery ---------------------------------------------------------- */

  class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.mainImage = this.querySelector('[data-main-image]');
      this.addEventListener('click', (event) => {
        const thumb = event.target.closest('[data-thumbnail]');
        if (!thumb) return;
        event.preventDefault();
        this.activate(thumb);
      });
      this.setupThumbArrows();
    }

    setupThumbArrows() {
      const track = this.querySelector('[data-thumbs-track]');
      const prev = this.querySelector('[data-thumbs-prev]');
      const next = this.querySelector('[data-thumbs-next]');
      if (!track || (!prev && !next)) return;

      // Animate the scroll manually; scroll-snap fights per-frame updates and
      // makes it look like a jump, so disable snapping while animating.
      const animateScrollTo = (target) => {
        const start = track.scrollLeft;
        const change = target - start;
        if (Math.abs(change) < 1) return;

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          track.scrollLeft = target;
          return;
        }

        track.style.scrollSnapType = 'none';
        const animId = (track._animId = (track._animId || 0) + 1);
        const duration = 350;
        let startTime = null;
        const tick = (time) => {
          if (track._animId !== animId) return;
          if (startTime === null) startTime = time;
          const progress = Math.min((time - startTime) / duration, 1);
          const ease = 1 - Math.pow(1 - progress, 3);
          track.scrollLeft = start + change * ease;
          if (progress < 1) {
            requestAnimationFrame(tick);
          } else {
            track.style.scrollSnapType = '';
          }
        };
        requestAnimationFrame(tick);
      };

      const scrollByPage = (direction) => {
        const thumb = track.querySelector('[data-thumbnail]');
        const gap = parseFloat(getComputedStyle(track).columnGap) || 12;
        const step = thumb ? (thumb.offsetWidth + gap) * 4 : track.clientWidth;
        const maxScroll = track.scrollWidth - track.clientWidth;
        const target = Math.max(0, Math.min(maxScroll, track.scrollLeft + direction * step));
        animateScrollTo(target);
      };

      if (prev) prev.addEventListener('click', () => scrollByPage(-1));
      if (next) next.addEventListener('click', () => scrollByPage(1));

      // Only show an arrow while sliding that direction is possible:
      // both stay hidden when everything fits, the left one appears after
      // sliding right, the right one disappears at the end.
      // 24px edge tolerance: scroll-snap can settle a few px short of the
      // exact end, which must still count as "at the edge".
      const EDGE = 24;
      const updateArrows = () => {
        const maxScroll = track.scrollWidth - track.clientWidth;
        const noOverflow = maxScroll <= EDGE;
        if (prev) prev.style.visibility = noOverflow || track.scrollLeft <= EDGE ? 'hidden' : 'visible';
        if (next) next.style.visibility = noOverflow || track.scrollLeft >= maxScroll - EDGE ? 'hidden' : 'visible';
      };
      track.addEventListener('scroll', updateArrows, { passive: true });
      window.addEventListener('resize', updateArrows);
      updateArrows();
    }

    activate(thumb) {
      if (!this.mainImage) return;
      this.mainImage.src = thumb.dataset.src;
      if (thumb.dataset.srcset) this.mainImage.srcset = thumb.dataset.srcset;
      this.mainImage.alt = thumb.dataset.alt || '';
      this.querySelectorAll('[data-thumbnail]').forEach((t) => t.setAttribute('aria-current', 'false'));
      thumb.setAttribute('aria-current', 'true');
      thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }

    activateByMediaId(mediaId) {
      const thumb = this.querySelector(`[data-thumbnail][data-media-id="${mediaId}"]`);
      if (thumb) this.activate(thumb);
      return !!thumb;
    }

    activateByIndex(index) {
      const thumbs = this.querySelectorAll('[data-thumbnail]');
      if (!thumbs[index]) return false;
      this.activate(thumbs[index]);
      return true;
    }
  }
  customElements.define('media-gallery', MediaGallery);

  /* --- Variant selects (dropdowns + swatch radios) ------------------------------ */

  class VariantSelects extends HTMLElement {
    constructor() {
      super();
      const dataEl = this.querySelector('script[type="application/json"]');
      this.variants = dataEl ? JSON.parse(dataEl.textContent) : [];
      this.sectionId = this.dataset.sectionId;
      this.productUrl = this.dataset.productUrl;
      this.addEventListener('change', this.onChange.bind(this));
    }

    get selectedOptions() {
      return Array.from(this.querySelectorAll('[data-option-group]'), (group) => {
        const select = group.querySelector('select');
        if (select) return select.value;
        const checked = group.querySelector('input[type="radio"]:checked');
        return checked ? checked.value : null;
      });
    }

    get currentVariant() {
      return this.variants.find((variant) =>
        variant.options.every((option, index) => option === this.selectedOptions[index])
      );
    }

    onChange() {
      const variant = this.currentVariant;
      this.updateHiddenInput(variant);
      this.updateButton(variant);
      this.updateMedia(variant);
      this.updateUrl(variant);
      if (variant) this.renderPrice(variant);
    }

    updateHiddenInput(variant) {
      const input = document.querySelector(`#product-form-${this.sectionId} input[name="id"]`);
      if (input && variant) {
        input.value = variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    updateButton(variant) {
      const button = document.querySelector(`#product-form-${this.sectionId} [type="submit"]`);
      if (!button) return;
      const buttonText = button.querySelector('span') || button;

      if (!variant) {
        button.setAttribute('disabled', 'disabled');
        buttonText.textContent = button.dataset.unavailableText;
      } else if (!variant.available) {
        button.setAttribute('disabled', 'disabled');
        buttonText.textContent = button.dataset.soldoutText;
      } else {
        button.removeAttribute('disabled');
        buttonText.textContent = button.dataset.addText;
      }
    }

    updateMedia(variant) {
      if (!variant) return;
      const gallery = document.querySelector(`media-gallery[data-section-id="${this.sectionId}"]`);
      if (!gallery) return;

      // 1. Variant image linked in Shopify (by media id, then by position)
      const media = variant.featured_media || variant.featured_image;
      if (media) {
        if (gallery.activateByMediaId(media.id)) return;
        if (media.position && gallery.activateByIndex(media.position - 1)) return;
      }

      // 2. Fallback: match the selected color name against thumbnail alt/filename
      const colorMap = {
        rood: 'red', groen: 'green', blauw: 'blue', goud: 'gold', zwart: 'black',
        wit: 'white', zilver: 'silver', grijs: 'grey', roze: 'pink', paars: 'purple',
        geel: 'yellow', oranje: 'orange', bruin: 'brown', beige: 'beige',
      };
      const thumbs = Array.from(gallery.querySelectorAll('[data-thumbnail]'));
      for (const value of this.selectedOptions) {
        if (!value) continue;
        const lower = value.toLowerCase();
        const terms = [lower];
        if (colorMap[lower]) terms.push(colorMap[lower]);
        const match = thumbs.find((t) => {
          const haystack = `${t.dataset.alt || ''} ${t.dataset.src || ''}`.toLowerCase();
          return terms.some((term) => haystack.includes(term));
        });
        if (match) {
          gallery.activate(match);
          return;
        }
      }
    }

    updateUrl(variant) {
      if (!variant) return;
      window.history.replaceState({}, '', `${this.productUrl}?variant=${variant.id}`);
    }

    async renderPrice(variant) {
      try {
        const response = await fetch(
          `${this.productUrl}?variant=${variant.id}&section_id=${this.sectionId}`
        );
        const text = await response.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const newPrice = doc.querySelector(`[data-price-wrapper="${this.sectionId}"]`);
        const currentPrice = document.querySelector(`[data-price-wrapper="${this.sectionId}"]`);
        if (newPrice && currentPrice) currentPrice.replaceWith(newPrice);

        const newSku = doc.querySelector(`[data-sku="${this.sectionId}"]`);
        const currentSku = document.querySelector(`[data-sku="${this.sectionId}"]`);
        if (newSku && currentSku) currentSku.replaceWith(newSku);
      } catch (error) {
        /* price stays as-is if render fails */
      }
    }
  }
  customElements.define('variant-selects', VariantSelects);

  /* --- Sticky order bar ----------------------------------------------------- */

  /* Mirrors the real buy button instead of reimplementing it: the price and the
     button state are copied from the live product form, so a variant change can
     never leave the two showing different things. */
  class StickyBuy extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.form = document.getElementById(`product-form-${this.sectionId}`);
      if (!this.form) return;

      this.mainButton = this.form.querySelector('[type="submit"]');
      this.mainPrice = document.querySelector(`[data-price-wrapper="${this.sectionId}"] .product__price`);
      this.price = this.querySelector('[data-sticky-price]');
      this.button = this.querySelector('[data-sticky-add]');
      this.label = this.querySelector('[data-sticky-label]');
      if (!this.mainButton) return;

      this.sync();

      const idInput = this.form.querySelector('input[name="id"]');
      if (idInput) idInput.addEventListener('change', () => this.sync());

      // Show only once the real button has scrolled out of view upwards, so the
      // bar never covers the button it duplicates.
      this.observer = new IntersectionObserver(
        ([entry]) => {
          const voorbij = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.toggle(voorbij);
        },
        { threshold: 0 }
      );
      this.observer.observe(this.mainButton);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      document.body.classList.remove('sticky-buy-visible');
    }

    toggle(zichtbaar) {
      this.hidden = !zichtbaar;
      this.classList.toggle('is-visible', zichtbaar);
      // Lets the WhatsApp button lift itself clear of the bar.
      document.body.classList.toggle('sticky-buy-visible', zichtbaar);
    }

    sync() {
      if (this.price && this.mainPrice) this.price.innerHTML = this.mainPrice.innerHTML;
      if (!this.button) return;

      const uit = this.mainButton.hasAttribute('disabled');
      this.button.toggleAttribute('disabled', uit);

      const bron = this.mainButton.querySelector('span');
      if (this.label && bron) this.label.textContent = bron.textContent;
    }
  }
  customElements.define('sticky-buy', StickyBuy);


  /* --- Tabs (Beschrijving / Specificaties) -------------------------------------- */

  class ProductTabs extends HTMLElement {
    constructor() {
      super();
      this.buttons = Array.from(this.querySelectorAll('.tabs__button'));
      this.addEventListener('click', (event) => {
        const button = event.target.closest('.tabs__button');
        if (!button) return;
        this.select(button);
      });
    }

    /* Sync panels to the selected tab on load as well. The initial hidden state
       used to live only in Liquid, so adding a tab in front of the others left
       two panels visible at once. */
    connectedCallback() {
      const selected = this.buttons.find((b) => b.getAttribute('aria-selected') === 'true');
      if (selected) this.select(selected);
    }

    select(button) {
      this.buttons.forEach((b) => {
        const panel = document.getElementById(b.getAttribute('aria-controls'));
        const active = b === button;
        b.setAttribute('aria-selected', active ? 'true' : 'false');
        if (panel) panel.hidden = !active;
      });
    }
  }
  customElements.define('product-tabs', ProductTabs);

  /* --- Read more toggle ----------------------------------------------------------- */

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-readmore-toggle]');
    if (!toggle) return;
    const content = toggle.parentElement.querySelector('[data-readmore-content]');
    if (!content) return;
    const clamped = content.classList.toggle('rte--clamp');
    toggle.textContent = clamped ? toggle.dataset.moreText : toggle.dataset.lessText;
  });

  /* --- Related products (Shopify recommendations) ---------------------------------- */

  class RelatedProducts extends HTMLElement {
    connectedCallback() {
      if (this.loaded) return;
      this.loaded = true;
      fetch(this.dataset.url)
        .then((response) => response.text())
        .then((text) => {
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const fresh = doc.querySelector('related-products');
          if (fresh && fresh.innerHTML.trim().length) {
            this.innerHTML = fresh.innerHTML;
            if (window.initCarousels) window.initCarousels();
          }
        })
        .catch(() => {
          /* leave section empty on failure */
        });
    }
  }
  customElements.define('related-products', RelatedProducts);
})();
